import { TagIcon } from "@phosphor-icons/react";
import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/data-display/tooltip";
import { cn } from "@/lib/utils";

const TAG_GAP_PX = 8;
const ROW_ITEM_GAP_PX = 8;
const TAG_MAX_LINES = 2;

interface ArticleTag {
  id: string;
  tag: string;
}

interface ArticleTagChipProps {
  label: string;
  measure?: boolean;
  tagId?: string;
}

const ArticleTagChip = ({
  label,
  measure = false,
  tagId,
}: ArticleTagChipProps) => (
  <span
    {...(measure ? { "data-tag-measure": true, "data-tag-id": tagId } : {})}
    className="inline-flex shrink-0 items-center gap-2 rounded-full border border-gray-200 bg-white px-2 py-1 text-xs font-medium tracking-wide text-gray-700"
  >
    <TagIcon className="size-3.5 shrink-0 text-gray-700" aria-hidden />
    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
      {label}
    </span>
  </span>
);

interface ArticleTagOverflowProps {
  overflowCount: number;
  overflowTags: ArticleTag[];
}

const ArticleTagOverflow = ({
  overflowCount,
  overflowTags,
}: ArticleTagOverflowProps) => {
  const [open, setOpen] = useState(false);

  const handleTooltipTriggerClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="shrink-0 cursor-pointer text-[11px] font-normal leading-4 tracking-[0.11px] text-gray-900 whitespace-nowrap"
            onClick={handleTooltipTriggerClick}
          >
            +{overflowCount} more
          </button>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent
            side="bottom"
            align="start"
            className="flex w-fit max-w-none flex-col items-start gap-3.5 p-3 shadow-lg"
          >
            {overflowTags.map((tag) => (
              <ArticleTagChip key={tag.id} label={tag.tag} />
            ))}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Returns whether sorted tag widths fit within the given line widths, reserving
 * overflow indicator space on the last line when needed.
 */
const canPackTags = (
  tagWidths: number[],
  lineWidths: number[],
  overflowCount: number,
  getOverflowWidth: (overflowCount: number) => number,
): boolean => {
  let tagIndex = 0;
  const numTags = tagWidths.length;
  const lastLineIndex = lineWidths.length - 1;

  for (let line = 0; line < lineWidths.length; line++) {
    let used = 0;
    const lineWidth = lineWidths[line] ?? 0;
    const isLastLine = line === lastLineIndex;
    const overflowW =
      isLastLine && overflowCount > 0 ? getOverflowWidth(overflowCount) : 0;

    while (tagIndex < numTags) {
      const tagW = tagWidths[tagIndex] ?? 0;
      const gapBefore = used > 0 ? TAG_GAP_PX : 0;
      const overflowReserve = overflowW > 0 ? TAG_GAP_PX + overflowW : 0;
      const needed = used + gapBefore + tagW + overflowReserve;

      if (needed <= lineWidth) {
        used += gapBefore + tagW;
        tagIndex++;
      } else {
        break;
      }
    }

    if (tagIndex >= numTags) {
      return true;
    }
  }

  return false;
};

export const getVisibleTagCount = (
  line1AvailableWidth: number,
  line2AvailableWidth: number,
  tagWidths: number[],
  getOverflowWidth: (overflowCount: number) => number,
): number => {
  const lineWidths = [line1AvailableWidth, line2AvailableWidth];
  const total = tagWidths.length;

  for (let visible = total; visible >= 0; visible--) {
    const overflow = total - visible;
    const widths = tagWidths.slice(0, visible);

    if (canPackTags(widths, lineWidths, overflow, getOverflowWidth)) {
      return visible;
    }
  }

  return 0;
};

export const splitTagsAcrossLines = (
  tagWidths: number[],
  line1AvailableWidth: number,
  line2AvailableWidth: number,
  overflowCount: number,
  getOverflowWidth: (overflowCount: number) => number,
): { line1Count: number; line2Count: number } => {
  const lineWidths = [line1AvailableWidth, line2AvailableWidth];
  const counts = [0, 0];
  let tagIndex = 0;
  const lastLineIndex = lineWidths.length - 1;

  for (let line = 0; line < TAG_MAX_LINES; line++) {
    let used = 0;
    const lineWidth = lineWidths[line] ?? 0;
    const isLastLine = line === lastLineIndex;
    const overflowW =
      isLastLine && overflowCount > 0 ? getOverflowWidth(overflowCount) : 0;

    while (tagIndex < tagWidths.length) {
      const tagW = tagWidths[tagIndex] ?? 0;
      const gapBefore = used > 0 ? TAG_GAP_PX : 0;
      const overflowReserve = overflowW > 0 ? TAG_GAP_PX + overflowW : 0;
      const needed = used + gapBefore + tagW + overflowReserve;

      if (needed <= lineWidth) {
        used += gapBefore + tagW;
        tagIndex++;
        counts[line] = (counts[line] ?? 0) + 1;
      } else {
        break;
      }
    }
  }

  return { line1Count: counts[0] ?? 0, line2Count: counts[1] ?? 0 };
};

const sortTagsByLength = (tags: ArticleTag[]) =>
  [...tags].sort((a, b) => {
    const lengthDiff = a.tag.length - b.tag.length;
    if (lengthDiff !== 0) {
      return lengthDiff;
    }

    return a.tag.localeCompare(b.tag);
  });

const getTagWidthsById = (measure: HTMLElement) => {
  const widths = new Map<string, number>();

  for (const element of measure.querySelectorAll<HTMLElement>(
    "[data-tag-measure]",
  )) {
    const tagId = element.dataset.tagId;
    if (!tagId) {
      continue;
    }

    widths.set(tagId, element.getBoundingClientRect().width);
  }

  return widths;
};

const getLineWidths = (
  wrapper: HTMLElement,
  badge: HTMLElement | null,
): { line1: number; line2: number } => {
  const wrapperWidth = wrapper.getBoundingClientRect().width;

  if (!badge) {
    return { line1: wrapperWidth, line2: wrapperWidth };
  }

  const badgeWidth = badge.getBoundingClientRect().width;

  return {
    line1: Math.max(0, wrapperWidth - badgeWidth - ROW_ITEM_GAP_PX),
    line2: wrapperWidth,
  };
};

export interface ArticleTagRowProps {
  tags: ArticleTag[];
  className?: string;
  /** Renders on the first row; remaining tag lines use the full row width. */
  badgeRow?: ReactNode;
}

export const ArticleTagRow = ({
  tags,
  className,
  badgeRow,
}: ArticleTagRowProps) => {
  const hasBadgeRow = Boolean(badgeRow);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const overflowMeasureRef = useRef<HTMLButtonElement>(null);
  const sortedTags = sortTagsByLength(tags);
  const [layout, setLayout] = useState({
    visibleCount: sortedTags.length,
    line1Count: sortedTags.length,
    line2Count: 0,
  });

  const updateLayout = useCallback(() => {
    const wrapper = wrapperRef.current;
    const measure = measureRef.current;
    const overflowMeasure = overflowMeasureRef.current;
    const tagsSortedByLength = sortTagsByLength(tags);

    if (
      !wrapper ||
      !measure ||
      !overflowMeasure ||
      tagsSortedByLength.length === 0
    ) {
      return;
    }

    const { line1, line2 } = getLineWidths(
      wrapper,
      hasBadgeRow ? badgeRef.current : null,
    );

    if (line1 === 0 && line2 === 0) {
      return;
    }

    const widthByTagId = getTagWidthsById(measure);
    if (widthByTagId.size !== tagsSortedByLength.length) {
      return;
    }

    const tagWidths = tagsSortedByLength.map(
      (tag) => widthByTagId.get(tag.id) ?? 0,
    );

    const getOverflowWidth = (overflowCount: number) => {
      overflowMeasure.textContent = `+${overflowCount} more`;
      return overflowMeasure.getBoundingClientRect().width;
    };

    const visibleCount = getVisibleTagCount(
      line1,
      line2,
      tagWidths,
      getOverflowWidth,
    );
    const overflowCount = tagsSortedByLength.length - visibleCount;
    const visibleWidths = tagWidths.slice(0, visibleCount);
    const { line1Count, line2Count } = splitTagsAcrossLines(
      visibleWidths,
      line1,
      line2,
      overflowCount,
      getOverflowWidth,
    );

    setLayout({ visibleCount, line1Count, line2Count });
  }, [tags, hasBadgeRow]);

  useLayoutEffect(() => {
    updateLayout();

    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateLayout);
    });

    resizeObserver.observe(wrapper);
    const badge = badgeRef.current;
    if (badge) {
      resizeObserver.observe(badge);
    }

    return () => resizeObserver.disconnect();
  }, [updateLayout]);

  if (tags.length === 0) {
    if (!badgeRow) {
      return null;
    }

    return (
      <div
        className={cn("relative min-w-0 w-full overflow-x-hidden", className)}
      >
        <div className="flex flex-col gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <div className="shrink-0">{badgeRow}</div>
          </div>
        </div>
      </div>
    );
  }

  const visibleTags = sortedTags.slice(0, layout.visibleCount);
  const line1Tags = visibleTags.slice(0, layout.line1Count);
  const line2Tags = visibleTags.slice(
    layout.line1Count,
    layout.line1Count + layout.line2Count,
  );
  const overflowTags = sortedTags.slice(layout.visibleCount);
  const overflowCount = overflowTags.length;

  return (
    <div
      ref={wrapperRef}
      className={cn("relative min-w-0 w-full overflow-x-hidden", className)}
    >
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute flex w-max gap-2 whitespace-nowrap"
      >
        {tags.map((tag) => (
          <ArticleTagChip key={tag.id} label={tag.tag} tagId={tag.id} measure />
        ))}
        <button
          ref={overflowMeasureRef}
          type="button"
          className="shrink-0 text-[11px] font-normal leading-4 tracking-[0.11px] whitespace-nowrap"
          tabIndex={-1}
        >
          +{tags.length} more
        </button>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {badgeRow ? (
            <div ref={badgeRef} className="shrink-0">
              {badgeRow}
            </div>
          ) : null}
          {line1Tags.length > 0 ? (
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
              {line1Tags.map((tag) => (
                <ArticleTagChip key={tag.id} label={tag.tag} />
              ))}
            </div>
          ) : null}
        </div>

        {line2Tags.length > 0 || overflowCount > 0 ? (
          <div className="flex items-center gap-2 overflow-hidden">
            {line2Tags.map((tag) => (
              <ArticleTagChip key={tag.id} label={tag.tag} />
            ))}
            {overflowCount > 0 ? (
              <ArticleTagOverflow
                overflowCount={overflowCount}
                overflowTags={overflowTags}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
